import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import usePrevious from 'hooks/usePrevious'
import useClient from 'hooks/useClient'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, Select } from 'antd'

import { getGovernorates } from 'redux/actions/governorates'
import { getDeliveryAreas, pullDeliveryAreas, updateDeliveryAreaGovernorate } from 'redux/actions/deliveryAreas'
import { PERMISSIONS } from 'utils/constants'

import SortableColumnTitle from 'components/SortableColumnTitle'
import PageTable from 'components/PageTable'
import Button from 'components/Button'

const Areas = () => {
  const { t } = useTranslation()
  const { isClientCan } = useClient()
  const dispatch = useDispatch()
  const [search, setSearch] = useState('')
  const prevSearch = usePrevious(search)
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [selectedArea, setSelectedArea] = useState(null)
  const [selectedGovernorateId, setSelectedGovernorateId] = useState(null)

  const { items, pagination, isGetLoading } = useSelector((state) => state.deliveryAreas)
  const { items: governorates } = useSelector((state) => state.governorates)

  const canPull = isClientCan(PERMISSIONS.UPDATE_CONFIGURATIONS)
  const canLink = isClientCan(PERMISSIONS.UPDATE_CONFIGURATIONS)

  const fetchPage = (page, size, search) => dispatch(getDeliveryAreas({ page, size, search }))

  useEffect(() => {
    if (!items.length) {
      fetchPage(pagination.current, pagination.pageSize, search)
    }
    dispatch(getGovernorates({ page: 1, size: 500 }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (prevSearch !== undefined && search !== prevSearch) {
      fetchPage(1, pagination.pageSize, search)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, prevSearch])

  const tableChangeHandler = (pagination) => fetchPage(pagination.current, pagination.pageSize, search)

  const columns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: '80px',
      align: 'center',
    },
    {
      title: (props) => <SortableColumnTitle {...props} title={t('locations.areaName')} id="name" />,
      dataIndex: 'name',
      key: 'name',
      sorter: true,
    },
    {
      title: (props) => (
        <SortableColumnTitle {...props} title={t('locations.areaNameAr')} id="arabianName" />
      ),
      dataIndex: 'arabianName',
      key: 'arabianName',
      sorter: true,
      render: (text) => (text ? <span dir="rtl">{text}</span> : '-'),
    },
    {
      title: t('locations.governorates'),
      dataIndex: ['governorate', 'code'],
      key: 'governorate',
      render: (text, record) => record?.governorate?.code || '-',
    },
  ]

  const governorateOptions = useMemo(() => {
    return (governorates || []).map((g) => ({
      value: g.id,
      label: `${g.code}${g.arabianName ? ` / ${g.arabianName}` : ''}`,
    }))
  }, [governorates])

  const openLinkModal = (area) => {
    if (!canLink) return
    setSelectedArea(area)
    setSelectedGovernorateId(area?.governorateId ?? null)
    setLinkModalOpen(true)
  }

  const closeLinkModal = () => {
    setLinkModalOpen(false)
    setSelectedArea(null)
    setSelectedGovernorateId(null)
  }

  const saveLink = () => {
    if (!selectedArea?.id) return
    dispatch(updateDeliveryAreaGovernorate({ id: selectedArea.id, governorateId: selectedGovernorateId }))
    closeLinkModal()
  }

  return (
    <>
      <PageTable
        pageTitle={t('locations.areas')}
        action={
          canPull ? (
            <Button type="primary" onClick={() => dispatch(pullDeliveryAreas())}>
              <span className="btn-text">{t('promotions.pull')}</span>
            </Button>
          ) : null
        }
        baseData={items}
        columns={columns}
        loading={isGetLoading}
        onChange={tableChangeHandler}
        onRow={(record) => ({
          onClick: () => openLinkModal(record),
        })}
        searchHandler={(value) => setSearch(value.trim())}
        pagination={{
          ...pagination,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          showSizeChanger: pagination.total >= 10,
        }}
      />

      <Modal
        title={t('locations.linkAreaToGovernorate')}
        visible={linkModalOpen}
        onCancel={closeLinkModal}
        onOk={saveLink}
        okButtonProps={{ disabled: !canLink }}
        destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600 }}>{selectedArea?.name || '-'}</div>
          <div style={{ color: '#666' }}>{selectedArea?.arabianName ? <span dir="rtl">{selectedArea.arabianName}</span> : '-'}</div>
        </div>

        <div style={{ marginBottom: 8 }}>{t('locations.governorates')}</div>
        <Select
          style={{ width: '100%' }}
          allowClear
          placeholder={t('locations.selectGovernorate')}
          value={selectedGovernorateId}
          onChange={(value) => setSelectedGovernorateId(value ?? null)}
          options={governorateOptions}
          showSearch
          optionFilterProp="label"
        />
      </Modal>
    </>
  )
}

export default Areas

